import { IGame } from "../types/game";
import GameState from "./game-state";
import { PacketTypes, MoveType } from "../../protocol/types";
import {
  StampBlockPacket,
  DeadPacket,
  PlayerMovePacket,
  GonePacket,
  RemoveBonusPacket,
  FirstFragPacket,
} from "../../protocol/packets";
import StampBlockState from "./stamp-block-state";
import DeadState from "./dead-state";
import FirstFragState from "./first-frag-state";

export default class WaitBlockState extends GameState {
  constructor(game: IGame) {
    super(game);
    this.canvas.idle = 1;

    if (this.canvas.moveIndex === undefined) {
      this.canvas.moveIndex = 0;
    }
  }

  enter() {}

  update(frameTime: number) {
    super.update(frameTime);

    let waitMore = false;

    if (this.game.playback) {
      let remain = this.canvas.daMoves.length - this.canvas.moveIndex;
      if (remain && remain <= 50) waitMore = true;
    }

    if (!waitMore) {
      if (this.check_gone()) return;
    }

    this.check_bonus();

    if (!waitMore) {
      const gameMode = this.game.getGameMode?.();
      if (gameMode === "survivor") {
        if (this.check_first_frag()) return;
      }
    }

    if (this.game.peekPacket && !this.game.playback) {
      const movePacket = this.game.peekPacket(
        PacketTypes.PLAYER_MOVE
      ) as PlayerMovePacket | null;

      const myPlayerNumber = this.canvas.numPlayer;

      if (movePacket && movePacket.numPlayer === myPlayerNumber) {
        const moveCount = movePacket.moves.length;
        if (this.canvas.daMoves.length + moveCount < 4000) {
          for (const move of movePacket.moves) {
            if (move.rawByte !== undefined) {
              this.canvas.daMoves.push(move.rawByte);
            } else {
              let moveByte = 0;
              switch (move.type) {
                case MoveType.MOVE_LEFT:
                  moveByte |= 2;
                  break;
                case MoveType.MOVE_RIGHT:
                  moveByte |= 4;
                  break;
                case MoveType.MOVE_DOWN:
                  moveByte |= 1;
                  break;
                case MoveType.DROP:
                  moveByte |= 64;
                  break;
                case MoveType.ROTATE_CW:
                  moveByte |= 16;
                  break;
                case MoveType.ROTATE_CCW:
                  moveByte |= 8;
                  break;
              }
              this.canvas.daMoves.push(moveByte);
            }
          }
        } else {
          const remotePlayerId = this.game.remotePlayerId;
          console.warn(
            `[WaitBlockState] Buffer full! Dropping ${moveCount} moves for remote player ${remotePlayerId}. ` +
              `Current buffer size: ${this.canvas.daMoves.length}, would exceed 4000 limit.`
          );
        }

        this.game.removePacket?.();
      }
    }

    const hasMoves = this.canvas.moveIndex < this.canvas.daMoves.length;
    const hasActiveBloc = !!this.canvas.bloc;
    const isPaused = (this.game as any).paused;

    if (hasMoves && !isPaused && hasActiveBloc && this.canvas.bloc) {
      const r: number = this.canvas.daMoves[this.canvas.moveIndex++];

      if (r & 8) this.rotateLeft();
      if (r & 16) this.rotateRight();
      if (r & 32) this.rotateRight(true);
      if (r & 1) this.moveDown();
      if (r & 64) this.dropDown();
      if (r & 2) this.moveLeft();
      if (r & 4) this.moveRight();

      let t: number = ((this.canvas.bloc.x - 4) << 4) * 18;
      let nx: number = this.canvas.bloc.posX;
      if (nx < t) {
        nx += this.canvas.sideSpeed;
        if (nx > t) nx = t;
      }
      if (nx > t) {
        nx -= this.canvas.sideSpeed;
        if (nx < t) nx = t;
      }

      t = this.calcBy(this.canvas.bloc.posY - (17 << 4) - 15);
      if (
        !this.canvas.checkCollide(
          this.canvas.bloc,
          this.canvas.bloc.x,
          t,
          this.canvas.bloc.rotation
        )
      )
        this.canvas.bloc.posX = nx;

      t = this.calcBy(this.canvas.bloc.posY + this.canvas.speed);
      if (
        !this.canvas.checkCollide(
          this.canvas.bloc,
          this.canvas.bloc.x,
          t,
          this.canvas.bloc.rotation
        )
      ) {
        this.canvas.bloc.posY += this.canvas.speed;
        this.canvas.bloc.y = this.calcBy(this.canvas.bloc.posY);
      }
      if (this.canvas.shadow) this.canvas.calcShadow(this.canvas.bloc);
    }

    if (waitMore) return;

    if (this.game.peekPacket) {
      const stampPacket = this.game.peekPacket(
        PacketTypes.STAMP_BLOCK
      ) as StampBlockPacket | null;

      const myPlayerNumber = this.canvas.numPlayer;

      if (stampPacket && stampPacket.numPlayer === myPlayerNumber) {
        this.call(new StampBlockState(this.game, stampPacket));
        this.ret();

        this.game.removePacket?.();
        return;
      }
    }

    if (this.game.peekPacket) {
      const deadPacket = this.game.peekPacket(
        PacketTypes.DEAD
      ) as DeadPacket | null;

      const myPlayerNumber = this.canvas.numPlayer;

      if (deadPacket && deadPacket.numPlayer === myPlayerNumber) {
        console.log(
          `[WaitBlockState] Processing P_DEAD for numPlayer=${myPlayerNumber}, thenGone=${deadPacket.thenGone}, transitioning to DeadState`
        );

        this.call(new DeadState(this.game, deadPacket.thenGone));
        this.ret();

        this.game.removePacket?.();
        return;
      } else if (deadPacket) {
        console.log(
          `[WaitBlockState] P_DEAD packet found but numPlayer mismatch: packet.numPlayer=${deadPacket.numPlayer}, myPlayerNumber=${myPlayerNumber}`
        );
      }
    }
  }

  /**
   * Check for gone packets (matches original player.cc:73-84)
   * Returns true if player is gone and state was transitioned
   */
  check_gone(): boolean {
    if (!this.game.peekPacket) return false;

    const gonePacket = this.game.peekPacket(
      PacketTypes.GONE
    ) as GonePacket | null;
    const connectionId = this.game.getConnectionId?.();

    if (
      gonePacket &&
      connectionId &&
      gonePacket.connectionId === connectionId
    ) {
      this.call(new DeadState(this.game, true));
      this.game.popState(this);

      this.game.removePacket?.();
      return true;
    }
    return false;
  }

  /**
   * Check for bonus removal packets (matches original player.cc:127-133)
   */
  check_bonus(): void {
    if (!this.game.peekPacket) return;

    const bonusPacket = this.game.peekPacket(
      PacketTypes.REMOVE_BONUS
    ) as RemoveBonusPacket | null;
    const connectionId = this.game.getConnectionId?.();

    if (
      bonusPacket &&
      connectionId &&
      bonusPacket.connectionId === connectionId
    ) {
      this.canvas.bonus = 0;
      this.game.removePacket?.();
    }
  }

  /**
   * Check for first frag packets (matches original player.cc:1380-1387)
   * Returns true if first frag received and state was transitioned
   */
  check_first_frag(): boolean {
    if (!this.game.peekPacket) return false;

    const fragPacket = this.game.peekPacket(
      PacketTypes.FIRST_FRAG
    ) as FirstFragPacket | null;
    const myPlayerNumber = this.canvas.numPlayer;

    if (fragPacket && fragPacket.numPlayer === myPlayerNumber) {
      console.log(
        `[WaitBlockState] First frag received for numPlayer=${myPlayerNumber}, transitioning to FirstFragState`
      );
      this.game.removePacket?.();

      this.call(new FirstFragState(this.game));
      this.ret();
      return true;
    }
    return false;
  }

  destructor() {
    const totalLeft =
      this.canvas.moveIndex < this.canvas.daMoves.length
        ? this.canvas.daMoves.length - this.canvas.moveIndex
        : 0;

    if (totalLeft > 0) {
    }

    this.canvas.daMoves = [];
    this.canvas.moveIndex = 0;
  }

  toString(): string {
    return "Wait Block";
  }
}
